/**
 * MetaApiAccountsSection — extracted from SettingsTab.jsx (iter-83).
 *
 * Bundles the FULL MetaApi-driven UI: the connected-accounts panel (with the
 * live ON/OFF toggle per account), plus the three dialogs that act on them:
 *   • Add MetaApi Account Dialog (creates a new remote account on MetaApi).
 *   • Manage Remote Accounts Dialog (slot-management, delete unused accounts).
 *   • Delete Confirmation AlertDialog (final guard before deletion).
 *
 * They share state (addAccountForm, accountToDelete, deletingAccountId,
 * remoteAccounts, etc.) so they live in one cohesive component.
 */
import { motion } from 'framer-motion';
import {
  Plug, CheckCircle, XCircle, Unplug, RefreshCw, Plus, Trash2,
  Wrench, AlertTriangle, Loader2, ListChecks, FlaskConical, Banknote, ShieldAlert
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';

export default function MetaApiAccountsSection({
  // status + lists
  connectionStatus, remoteAccounts, loadingRemoteAccounts, fetchRemoteAccounts,
  totalSlotsUsed, t, language,
  // actions
  switchToRemoteAccount, handleMt5Disconnect, mt5Connecting,
  // add dialog
  addAccountOpen, setAddAccountOpen, addAccountForm, setAddAccountForm,
  handleAddAccount, addingAccount,
  // manage dialog
  manageOpen, setManageOpen,
  // delete confirm
  accountToDelete, setAccountToDelete, handleDeleteRemoteAccount, deletingAccountId,
}) {
  return (
    <>
      {/* MetaApi Connected Accounts — switch with one tap */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plug className="h-5 w-5 text-[#d4af37]" />
            {t.mt5Connection}
          </h3>
          {connectionStatus && (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
              connectionStatus.connected
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {connectionStatus.connected ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {connectionStatus.connected ? t.connected : t.disconnected}
              {connectionStatus.mode && ` (${connectionStatus.mode})`}
            </div>
          )}
        </div>

        {/* Account Picker (from MetaApi) */}
        <div className="mb-5" data-testid="mt5-account-picker">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-[#d4af37]" />
              <h4 className="text-sm font-semibold text-white">
                {language === 'ar' ? 'الحسابات المسجلة في MetaApi' : 'MetaApi Registered Accounts'}
              </h4>
              {remoteAccounts.length > 0 && (
                <span className="px-2 py-0.5 bg-[#d4af37]/10 text-[#d4af37] text-xs rounded-full">
                  {remoteAccounts.length}
                </span>
              )}
              {remoteAccounts.length > 0 && (
                <span
                  className={`px-2 py-0.5 text-[10px] rounded-full font-semibold ${
                    totalSlotsUsed >= 8
                      ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                      : totalSlotsUsed >= 5
                      ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                      : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                  }`}
                  data-testid="mt5-slots-used-badge"
                  title={language === 'ar' ? 'إجمالي slots المستخدمة من خطة MetaApi' : 'Total resource slots used on your MetaApi plan'}
                >
                  {language === 'ar' ? `slots: ${totalSlotsUsed}` : `${totalSlotsUsed} slots`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                onClick={() => {
                  setAddAccountForm({ name: '', login: '', password: '', server: '', mode: 'live', platform: 'mt5', connect_now: true });
                  setAddAccountOpen(true);
                }}
                size="sm"
                className="bg-red-500 hover:bg-red-600 text-white border-0 font-semibold shadow-[0_0_18px_rgba(239,68,68,0.25)]"
                data-testid="mt5-add-live-account-btn"
                title={language === 'ar' ? 'إضافة حساب MT5 حقيقي (Live)' : 'Add a real (Live) MT5 account'}
              >
                <Banknote className="h-3.5 w-3.5 me-1" />
                {language === 'ar' ? 'إضافة حساب حقيقي' : 'Add Real Account'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setAddAccountForm({ name: '', login: '', password: '', server: '', mode: 'demo', platform: 'mt5', connect_now: true });
                  setAddAccountOpen(true);
                }}
                size="sm"
                variant="outline"
                className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
                data-testid="mt5-add-demo-account-btn"
                title={language === 'ar' ? 'إضافة حساب MT5 تجريبي (Demo)' : 'Add a demo MT5 account'}
              >
                <FlaskConical className="h-3.5 w-3.5 me-1" />
                {language === 'ar' ? 'إضافة حساب تجريبي' : 'Add Demo Account'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  fetchRemoteAccounts();
                  setManageOpen(true);
                }}
                size="sm"
                variant="outline"
                className="border-amber-400/30 text-amber-300 hover:bg-amber-400/10"
                data-testid="mt5-manage-accounts-btn"
                title={language === 'ar' ? 'إدارة حسابات MetaApi (احذف الحسابات القديمة لتحرير slots)' : 'Manage MetaApi accounts (delete to free resource slots)'}
              >
                <Wrench className="h-3.5 w-3.5 me-1" />
                {language === 'ar' ? 'إدارة' : 'Manage'}
              </Button>
              <Button
                type="button"
                onClick={fetchRemoteAccounts}
                disabled={loadingRemoteAccounts}
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
                data-testid="mt5-fetch-accounts-btn"
              >
                {loadingRemoteAccounts ? (
                  <Loader2 className="h-3 w-3 animate-spin me-2" />
                ) : (
                  <RefreshCw className="h-3 w-3 me-2" />
                )}
                {language === 'ar' ? 'تحديث' : 'Refresh'}
              </Button>
            </div>
          </div>

          {remoteAccounts.length === 0 ? (
            <p className="text-xs text-[#71717A] bg-white/[0.02] border border-white/10 rounded-lg p-3">
              {language === 'ar'
                ? 'اضغط "تحديث القائمة" لجلب الحسابات المضافة على لوحة MetaApi.cloud.'
                : 'Click "Refresh List" to load accounts from your MetaApi.cloud dashboard.'}
            </p>
          ) : (
            <div className="space-y-2" data-testid="mt5-account-picker-list">
              {remoteAccounts.map((acc, idx) => {
                // `is_current` is the source of truth from the backend — true ONLY when
                // this account is the one currently live-connected to the MetaApi broker.
                const isOn = !!acc.is_current;
                const isPending = mt5Connecting;
                return (
                  <div
                    key={acc.account_id || `${acc.login}-${acc.server}-${idx}`}
                    data-testid={`mt5-account-${acc.login}`}
                    className={`w-full p-3 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                      isOn
                        ? 'bg-emerald-500/10 border-emerald-500/40 shadow-[0_0_16px_rgba(16,185,129,0.18)]'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                    }`}
                  >
                    {/* Account meta — clicking it does nothing; the toggle drives state */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-white font-semibold">{acc.login}</span>
                        <span className="text-xs text-[#71717A]">·</span>
                        <span className="text-xs text-white truncate max-w-[160px]">{acc.server}</span>
                        <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                          acc.mode === 'live'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {acc.mode === 'live' ? 'LIVE' : 'DEMO'}
                        </span>
                        {isOn && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-300 font-bold">
                            {language === 'ar' ? '● متصل' : '● Active'}
                          </span>
                        )}
                      </div>
                      {acc.name && (
                        <p className="text-[10px] text-[#71717A] mt-1 truncate">{acc.name}</p>
                      )}
                    </div>

                    {/* ON / OFF Toggle Switch */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isOn}
                      disabled={isPending}
                      onClick={() => {
                        if (isOn) {
                          handleMt5Disconnect();
                        } else {
                          switchToRemoteAccount(acc);
                        }
                      }}
                      data-testid={`mt5-account-toggle-${acc.login}`}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isOn
                          ? 'bg-emerald-500 border-emerald-400'
                          : 'bg-white/10 border-white/20'
                      }`}
                    >
                      {isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-white mx-auto" />
                      ) : (
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                            isOn ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {connectionStatus?.broker === 'metaapi' && (
          <div className="mt-4 flex gap-3">
            <Button
              onClick={handleMt5Disconnect}
              disabled={mt5Connecting}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
              data-testid="mt5-disconnect-btn"
            >
              <Unplug className="h-4 w-4 mr-2" />
              {language === 'ar' ? 'قطع الاتصال' : 'Disconnect'}
            </Button>
          </div>
        )}

        {connectionStatus?.broker === 'metaapi' ? (
          <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-emerald-400 text-sm">
              {language === 'ar'
                ? `✅ متصل بحساب حقيقي عبر MetaApi — حساب ${connectionStatus.login} على ${connectionStatus.server} (وضع ${connectionStatus.mode === 'demo' ? 'تجريبي' : 'حقيقي'})`
                : `✅ Connected to real account via MetaApi — ${connectionStatus.login} @ ${connectionStatus.server} (${connectionStatus.mode})`}
            </p>
          </div>
        ) : connectionStatus?.metaapi_token_configured === false ? (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-amber-400 text-sm">
              {language === 'ar'
                ? '⚠️ مفتاح MetaApi غير مهيأ. أضِف بروفايل مفتاح في القسم أعلاه.'
                : '⚠️ MetaApi token not configured. Add a key profile in the section above.'}
            </p>
          </div>
        ) : (
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-blue-400 text-sm">
              {language === 'ar'
                ? 'اختر حساباً من القائمة أعلاه واضغط زر "تبديل" للاتصال — البوت سيُوقَف تلقائياً قبل التبديل.'
                : 'Pick an account above and tap "Switch" to connect — the bot is auto-stopped before switching.'}
            </p>
          </div>
        )}
      </motion.div>

      {/* Add MetaApi Account Dialog */}
      <Dialog open={addAccountOpen} onOpenChange={setAddAccountOpen}>
        <DialogContent className="bg-[#0a0a14] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              {addAccountForm.mode === 'live' ? (
                <Banknote className="h-5 w-5 text-red-400" />
              ) : (
                <FlaskConical className="h-5 w-5 text-amber-400" />
              )}
              {addAccountForm.mode === 'live'
                ? (language === 'ar' ? 'إضافة حساب MT5 حقيقي (Live)' : 'Add a real MT5 account (Live)')
                : (language === 'ar' ? 'إضافة حساب MT5 تجريبي (Demo)' : 'Add a demo MT5 account')}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] text-xs pt-2">
              {language === 'ar'
                ? 'يتم تسجيل الحساب على MetaApi.cloud باستخدام التوكن المُفعّل، ثم يمكن التبديل إليه فوراً. تأكد أن بيانات الحساب صحيحة لدى البروكر.'
                : 'Account will be registered with MetaApi.cloud using your active token. Make sure credentials are valid at your broker.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs text-[#71717A] mb-1.5">
                {language === 'ar' ? 'الاسم (اختياري)' : 'Account Label (optional)'}
              </label>
              <Input
                value={addAccountForm.name}
                onChange={(e) => setAddAccountForm({ ...addAccountForm, name: e.target.value })}
                placeholder={language === 'ar' ? 'مثال: حسابي الحقيقي' : 'e.g. My Live Account'}
                className="bg-white/5 border-white/10 text-white"
                data-testid="add-account-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-[#71717A] mb-1.5">
                  {language === 'ar' ? 'رقم الحساب' : 'Login Number'} <span className="text-red-400">*</span>
                </label>
                <Input
                  type="number"
                  value={addAccountForm.login}
                  onChange={(e) => setAddAccountForm({ ...addAccountForm, login: e.target.value })}
                  placeholder="12345678"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="add-account-login"
                />
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1.5">
                  {language === 'ar' ? 'كلمة السر' : 'Password'} <span className="text-red-400">*</span>
                </label>
                <Input
                  type="password"
                  value={addAccountForm.password}
                  onChange={(e) => setAddAccountForm({ ...addAccountForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="add-account-password"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-[#71717A] mb-1.5">
                {language === 'ar' ? 'اسم السيرفر' : 'Server Name'} <span className="text-red-400">*</span>
              </label>
              <Input
                value={addAccountForm.server}
                onChange={(e) => setAddAccountForm({ ...addAccountForm, server: e.target.value })}
                placeholder="MEXGlobalFinancial-Live"
                className="bg-white/5 border-white/10 text-white"
                data-testid="add-account-server"
              />
              <p className="text-[10px] text-[#71717A] mt-1">
                {language === 'ar' ? 'كما يظهر في برنامج MetaTrader (مثلاً: MEXGlobalFinancial-Live).' : 'As shown in your MetaTrader terminal.'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-[#71717A] mb-1.5">
                  {language === 'ar' ? 'النوع' : 'Mode'}
                </label>
                <Select value={addAccountForm.mode} onValueChange={(v) => setAddAccountForm({ ...addAccountForm, mode: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="add-account-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                    <SelectItem value="demo">{language === 'ar' ? 'تجريبي (DEMO)' : 'Demo'}</SelectItem>
                    <SelectItem value="live">{language === 'ar' ? 'حقيقي (LIVE)' : 'Live'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1.5">
                  {language === 'ar' ? 'المنصة' : 'Platform'}
                </label>
                <Select value={addAccountForm.platform} onValueChange={(v) => setAddAccountForm({ ...addAccountForm, platform: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="add-account-platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                    <SelectItem value="mt5">MetaTrader 5</SelectItem>
                    <SelectItem value="mt4">MetaTrader 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {addAccountForm.mode === 'live' && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-400 text-xs">
                  {language === 'ar'
                    ? 'تنبيه: الوضع الحقيقي. أي صفقة سيتم تنفيذها بأموال فعلية.'
                    : 'Warning: LIVE mode. Trades use real money.'}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setAddAccountOpen(false)}
              disabled={addingAccount}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="add-account-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAddAccount}
              disabled={addingAccount}
              className={`${addAccountForm.mode === 'live' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
              data-testid="add-account-submit"
            >
              {addingAccount ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Plus className="h-4 w-4 me-2" />}
              {language === 'ar' ? 'إضافة و تفعيل' : 'Add & Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Remote MetaApi Accounts Dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-[#0a0a14] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="mt5-manage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Wrench className="h-5 w-5 text-amber-400" />
              {language === 'ar' ? 'إدارة حسابات MetaApi' : 'Manage MetaApi Accounts'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] text-xs pt-2">
              {language === 'ar'
                ? 'هذه الحسابات مُسجّلة على خطتك في MetaApi.cloud وكلٌّ منها يستهلك resource slot. احذف الحسابات القديمة غير المستخدمة لتحرير slots وإضافة حسابات جديدة.'
                : 'These accounts live on your MetaApi.cloud plan and each consumes resource slots. Delete the ones you no longer use to free slots for new accounts.'}
            </DialogDescription>
          </DialogHeader>

          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2 my-2">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-amber-300 text-xs space-y-1">
              <p className="font-semibold">
                {language === 'ar'
                  ? `إجمالي slots المستخدمة الآن: ${totalSlotsUsed}`
                  : `Slots currently used: ${totalSlotsUsed}`}
              </p>
              <p className="text-amber-300/80">
                {language === 'ar'
                  ? 'الحذف عملية نهائية على MetaApi — لكن البيانات (الصفقات/البالنس) تبقى عند البروكر نفسه.'
                  : 'Delete is permanent on MetaApi — but your broker keeps the actual account (history & balance).'}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-[#71717A]">
              {language === 'ar'
                ? `إجمالي الحسابات: ${remoteAccounts.length}`
                : `Total accounts: ${remoteAccounts.length}`}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={fetchRemoteAccounts}
              disabled={loadingRemoteAccounts}
              className="border-white/10 text-white hover:bg-white/5 h-7"
              data-testid="mt5-manage-refresh-btn"
            >
              {loadingRemoteAccounts ? (
                <Loader2 className="h-3 w-3 animate-spin me-1" />
              ) : (
                <RefreshCw className="h-3 w-3 me-1" />
              )}
              {language === 'ar' ? 'تحديث' : 'Refresh'}
            </Button>
          </div>

          {remoteAccounts.length === 0 ? (
            <p className="text-sm text-[#71717A] bg-white/[0.02] border border-white/10 rounded-lg p-4 text-center">
              {language === 'ar' ? 'لا توجد حسابات مسجلة في MetaApi.' : 'No accounts registered on MetaApi.'}
            </p>
          ) : (
            <div className="space-y-2" data-testid="mt5-manage-accounts-list">
              {remoteAccounts.map((acc) => {
                const isActive = !!acc.is_current;
                const isDeleting = deletingAccountId === acc.account_id;
                return (
                  <div
                    key={acc.account_id}
                    data-testid={`mt5-manage-row-${acc.login}`}
                    className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${
                      isActive
                        ? 'bg-emerald-500/10 border-emerald-500/40'
                        : 'bg-white/[0.02] border-white/10'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-white font-semibold">{acc.login}</span>
                        <span className="text-xs text-[#71717A]">·</span>
                        <span className="text-xs text-white truncate max-w-[180px]">{acc.server}</span>
                        <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                          acc.mode === 'live'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {acc.mode === 'live' ? 'LIVE' : 'DEMO'}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-white/5 text-[#A1A1AA] border border-white/10">
                          {language === 'ar' ? `${acc.resource_slots || 1} slot` : `${acc.resource_slots || 1} slot${(acc.resource_slots || 1) > 1 ? 's' : ''}`}
                        </span>
                        {isActive && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-300 font-bold">
                            {language === 'ar' ? '● متصل الآن' : '● Active'}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#71717A] mt-1 truncate">
                        {acc.name || '—'} {acc.state ? ` · ${acc.state}` : ''} {acc.region ? ` · ${acc.region}` : ''}
                      </p>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setAccountToDelete(acc)}
                      disabled={isActive || isDeleting}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`mt5-manage-delete-${acc.login}`}
                      title={isActive
                        ? (language === 'ar' ? 'افصل الحساب أولاً قبل الحذف' : 'Disconnect first before deleting')
                        : (language === 'ar' ? 'حذف الحساب نهائياً من MetaApi' : 'Permanently delete from MetaApi')}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setManageOpen(false)}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="mt5-manage-close-btn"
            >
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!accountToDelete} onOpenChange={(open) => !open && setAccountToDelete(null)}>
        <AlertDialogContent className="bg-[#0a0a14] border-red-500/30 text-white" data-testid="mt5-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              {language === 'ar' ? 'تأكيد حذف الحساب' : 'Confirm Account Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA] text-sm pt-2 space-y-2">
              <span className="block">
                {language === 'ar' ? 'سيتم حذف الحساب التالي نهائياً من MetaApi:' : 'The following account will be permanently removed from MetaApi:'}
              </span>
              {accountToDelete && (
                <span className="block bg-white/5 border border-white/10 rounded-lg p-3 font-mono text-white">
                  {accountToDelete.login} · {accountToDelete.server} ({accountToDelete.mode?.toUpperCase()})
                </span>
              )}
              <span className="block text-amber-300/90 text-xs">
                {language === 'ar'
                  ? 'ملاحظة: الحساب لدى البروكر يبقى كما هو — هذا الإجراء فقط يحرّر slot واحد (أو أكثر) من خطة MetaApi لاستخدامه في حسابات جديدة.'
                  : 'Note: Your broker still keeps the actual account — this only frees the MetaApi resource slot so you can register new accounts.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="mt5-delete-cancel-btn"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRemoteAccount}
              disabled={!!deletingAccountId}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="mt5-delete-confirm-btn"
            >
              {deletingAccountId ? (
                <Loader2 className="h-4 w-4 animate-spin me-2" />
              ) : (
                <Trash2 className="h-4 w-4 me-2" />
              )}
              {language === 'ar' ? 'حذف نهائياً' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>    </>
  );
}
